
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/auth-context";
import { Agent } from "@/models/agent";
import { AgentService } from "@/services/agent-service";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import LoadingSpinner from "@/components/agent-status/LoadingSpinner";
import ErrorCard from "@/components/agent-status/ErrorCard";
import ApprovalProcessCard from "@/components/agent-status/ApprovalProcessCard";
import AgentDetailsCard from "@/components/agent-status/AgentDetailsCard";

const AgentStatus: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchAgent = async () => {
      try {
        if (!agentId) {
          throw new Error("No agent ID provided");
        }
        
        const fetchedAgent = await AgentService.getAgentById(agentId);
        if (!fetchedAgent) {
          throw new Error("Agent not found");
        }
        
        // Validate that the current user is the owner
        if (fetchedAgent.owner_id !== user?.id) {
          throw new Error("You don't have permission to view this agent's status");
        }
        
        setAgent(fetchedAgent);
      } catch (err: any) {
        console.error("Failed to fetch agent:", err);
        setError(err.message || "Failed to load agent data");
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchAgent();
    } else {
      setLoading(false);
      setError("You must be logged in to view agent status");
    }
  }, [agentId, user]);
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (error) {
    return (
      <div className="container py-12">
        <ErrorCard error={error} />
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>Agent Status - OGMAI</title>
      </Helmet>
      <div className="container py-8">
        <div className="mb-6">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-2 mb-4" 
            onClick={() => navigate("/my-agents")}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to My Agents
          </Button>
          
          <h1 className="text-3xl font-bold">{agent?.name || "Agent"} Status</h1>
          <p className="text-muted-foreground mt-1">
            Track the approval process of your agent
          </p>
        </div>
        
        <div className="grid gap-6 md:grid-cols-7">
          <div className="md:col-span-5">
            <ApprovalProcessCard agentId={agentId} />
          </div>
          
          <div className="md:col-span-2">
            <AgentDetailsCard agent={agent} />
          </div>
        </div>
      </div>
    </>
  );
};

export default AgentStatus;
